// Public types for the write/publish path (szzt-spec.md §4, §7, §8, §15).
//
// The publish logic depends only on the minimal `PdsClient` seam below, never directly
// on a network library — so the orchestration is unit-testable against a fake PDS and the
// atproto client is a thin, swappable adapter (see client.ts). Credentials live in the
// client the caller constructs; `publish`/`rollback` never see them (§15).

import type { BuildResult } from "@szzt/core";
import type {
  StrongRef,
  CidLink,
  FetchedRecord,
  MaslRecordValue,
  DeploymentRecordValue,
  SiteRecordValue,
} from "@szzt/records";
// re-export the shared shapes under publish's historical names
export type { StrongRef, CidLink, FetchedRecord };
export type MaslRecord = MaslRecordValue;
export type SiteRecord = SiteRecordValue;
// publish ALWAYS sets entry + tile; narrow the read-lenient optional shape to required for builder return types
export type DeploymentRecord = DeploymentRecordValue & { entry: string; tile: StrongRef };

/** A single write in an atomic `applyWrites` (com.atproto.repo.applyWrites). */
export type ApplyWrite =
  | { action: "create"; collection: string; rkey: string; value: unknown }
  | { action: "update"; collection: string; rkey: string; value: unknown }
  | { action: "delete"; collection: string; rkey: string };

/** The current repo head, used as an `applyWrites` `swapCommit` guard (§7.1). */
export interface CommitRef {
  cid: string;
  rev: string;
}

/** Canonical PDS blob read, not a mirror or local upload cache. Null means definitive absence;
 * authorization, rate-limit and transport failures must throw. Bytes are hash-checked by callers. */
export interface PdsBlobReader {
  getBlob(cid: string): Promise<Uint8Array | null>;
}

/**
 * Minimal PDS surface the write path needs. An adapter over `@atproto/api` (or any atproto
 * client) implements this; tests supply a fake. All CIDs are base32 CIDv1 strings.
 */
export interface PdsClient {
  /** DID of the authenticated repo being written to. */
  readonly did: string;
  /** Optional for backwards-compatible write-only adapters; required by `verifyBlobs: true`. */
  getBlob?: PdsBlobReader["getBlob"];
  /**
   * Store raw bytes as a blob and return its CID + size. A conforming PDS returns the
   * CIDv1/`raw`/sha-256 CID of the exact bytes (§7.2); the caller MUST verify it matches.
   */
  uploadBlob(bytes: Uint8Array, contentType?: string): Promise<{ cid: string; size: number }>;
  /** Current repo head commit (com.atproto.sync.getLatestCommit), for the `swapCommit` guard. */
  getLatestCommit(): Promise<CommitRef>;
  /** Atomic multi-record write. When `swapCommit` is set and does not match head, throws `SwapFailedError`. */
  applyWrites(input: { writes: ApplyWrite[]; swapCommit?: string }): Promise<{ commit?: CommitRef }>;
  /** Fetch a single record, or `null` if it does not exist. */
  getRecord(collection: string, rkey: string): Promise<FetchedRecord | null>;
  /**
   * Guarded single-record put (com.atproto.repo.putRecord). When `swapRecord` is set and does
   * not match the record's current CID, throws `SwapFailedError`. Pass `null` to require absence.
   * `swapCommit` guards the repo HEAD in the same call: it fails the write on ANY concurrent repo
   * write, not just one to this record — which is what lets a put also defend a decision it made by
   * reading OTHER records first (see `rollback`, which reads the target deployment).
   */
  putRecord(input: {
    collection: string;
    rkey: string;
    value: unknown;
    swapRecord?: string | null;
    swapCommit?: string;
  }): Promise<{ uri: string; cid: string }>;
}

/** One page of a repo listing (com.atproto.repo.listRecords). */
export interface RecordPage {
  records: readonly FetchedRecord[];
  /** Cursor for the next page; absent on the last page. */
  cursor?: string;
}

/**
 * Minimal repo-ENUMERATION surface, deliberately separate from {@link PdsClient}. Only the NSID
 * migration needs to walk a whole collection; the publish/rollback/prune path never does, and
 * `planPrune`/`planRetention` take the enumerated graph as data rather than listing it themselves.
 * Keeping this its own interface means the write seam stays exactly as narrow as it was, and an
 * implementor of `PdsClient` is not forced to grow a method it has no use for.
 */
export interface RepoRecordLister {
  /** One page of records in `collection`, oldest-first, from `cursor` when supplied. */
  listRecords(input: { collection: string; limit?: number; cursor?: string }): Promise<RecordPage>;
}

/**
 * Default target-PDS record-size ceiling for the §3.7 pre-publish manifest-size check, in bytes.
 *
 * The `ing.dasl.masl` record carries the MASL inline, so the encoded manifest dominates the record's
 * size. atproto's data-validation guidance defines a recommended maximum CBOR record size
 * (`MAX_CBOR_RECORD_SIZE`) of 1 MiB — a conforming PDS rejects a larger record at `applyWrites` — and
 * an `applyWrites` request body is itself capped at ~1 MB. So 1 MiB is the conservative default; a
 * caller targeting a PDS with a different limit passes {@link PublishInput.maxRecordBytes}.
 * Source: https://atproto.com/guides/data-validation (`MAX_CBOR_RECORD_SIZE` = 1,048,576 bytes).
 */
export const MAX_RECORD_BYTES = 1_048_576;

/**
 * Thrown when the encoded manifest exceeds the target PDS's record-size limit (§3.7). The manifest is
 * inline in the `ing.dasl.masl` record, so an over-limit manifest is rejected by the PDS at
 * `applyWrites` — but only AFTER every resource blob has been uploaded. `publish` validates the size
 * BEFORE any upload and throws this, so a path-heavy site fails fast with a clear error instead of
 * wasting the whole blob upload on a record the PDS will refuse. Closes §3.7 / conformance gap G10.
 */
export class ManifestTooLargeError extends Error {
  constructor(
    readonly encodedManifestSize: number,
    readonly limit: number,
  ) {
    super(
      `encoded manifest is ${encodedManifestSize} bytes, exceeds the target PDS record-size limit ` +
        `of ${limit} bytes (§3.7) — refusing to upload blobs for a record the PDS would reject at applyWrites`,
    );
    this.name = "ManifestTooLargeError";
  }
}

/** Thrown by a `PdsClient` when a `swapCommit`/`swapRecord` compare-and-swap guard fails (§7.1). */
export class SwapFailedError extends Error {
  constructor(message = "compare-and-swap guard failed (concurrent write)") {
    super(message);
    this.name = "SwapFailedError";
  }
}

/**
 * Thrown when a `BuildResult` is self-inconsistent: its declared `rootCid` does not equal
 * `dagCbor(its own manifest)`. Publishing such a build would write a manifest record that every
 * gateway rejects at serve time (§9.2 fail-closed → 502). `publish` refuses it BEFORE any write.
 */
export class ManifestIntegrityError extends Error {
  constructor(
    readonly recomputedRootCid: string,
    readonly declaredRootCid: string,
  ) {
    super(
      `self-inconsistent build: dagCbor(manifest)=${recomputedRootCid} != rootCid=${declaredRootCid} ` +
        `(§9.2) — refusing to write a manifest record the gateway would reject`,
    );
    this.name = "ManifestIntegrityError";
  }
}

/**
 * What the post-verification-failure cleanup actually did. Reported on every
 * {@link PublishVerificationError} because the cleanup has three genuinely different outcomes and
 * two of them leave records behind — a caller that assumes "threw ⇒ fully undone" would be wrong.
 *
 * - `undone` — the site pointer still named our deployment, so our records were removed and any
 *   prior deployment restored. The repo is back to its pre-publish state.
 * - `skipped` — the live site no longer exactly matches our write (including metadata-only edits).
 *   We touched nothing: restoring it could overwrite an independent edit or dangle later history.
 *   Retained records may still be live; inspect them before any manual cleanup.
 * - `failed` — the guarded cleanup write itself did not land. Our rejected records are STILL LIVE
 *   and the site may still point at them. The underlying error is attached as `cause`.
 */
export type CleanupOutcome =
  | { status: "undone" }
  | { status: "skipped"; reason: string }
  | { status: "failed"; error: unknown };

/** One-line, human-readable tail describing a {@link CleanupOutcome} for an error message. */
function describeCleanup(outcome: CleanupOutcome): string {
  switch (outcome.status) {
    case "undone":
      return "restored the prior site state and removed this publish's records";
    case "skipped":
      return (
        `cleanup SKIPPED and the rejected records left in place — ${outcome.reason}. ` +
        `Records were retained; inspect the live state before any manual cleanup`
      );
    case "failed":
      return (
        "cleanup ALSO FAILED (see `cause`) — the rejected records are still live and the site " +
        "may still point at them; re-publish or roll back manually"
      );
  }
}

/**
 * Thrown when post-write record-chain inspection fails (including an unavailable readback).
 * `publish` attempts guarded cleanup and preserves concurrent site edits. This is PDS-reported
 * inspection, not independent MST verification. `cleanup` reports what was actually undone.
 */
export class PublishVerificationError extends Error {
  constructor(
    reason: string,
    readonly cleanup: CleanupOutcome = { status: "undone" },
  ) {
    super(
      `published record chain failed read-back verification (§9.2) — ${reason}; ` +
        describeCleanup(cleanup),
      cleanup.status === "failed" ? { cause: cleanup.error } : undefined,
    );
    this.name = "PublishVerificationError";
  }
}

/** Site-level metadata carried on the `app.szzt.site` record (§4.1). */
export interface SiteMeta {
  title?: string;
  slug?: string;
  hints?: string[];
}

/**
 * One content-addressed block to push to an external mirror (BYO producer, §7.3 / §13.1). `cid` is
 * the DASL CID of `bytes` under `codec`: `raw` (0x55) for a resource blob, `dag-cbor` (0x71) for the
 * manifest. A producer MUST store the block so it later resolves under EXACTLY this `cid` — for IPFS
 * that means storing a single raw/dag-cbor block (not a UnixFS `dag-pb` upload, which re-chunks and
 * re-roots any file >256 KiB under a different CID; §3.2 restricts our CIDs to that raw subset).
 */
export interface MirrorBlock {
  cid: string;
  bytes: Uint8Array;
  codec: "raw" | "dag-cbor";
  /** Resource MIME type (§3.3); absent for the manifest block. */
  contentType?: string;
}

/**
 * A best-effort external mirror producer (BYO CDN or IPFS, spec §7.3). `publish()` pushes every
 * resource blob AND the manifest through this seam, then — only if ALL pushes succeed — records
 * `hints()` on the site record so gateways can race the mirror. Transport-agnostic: a RASL/CDN
 * producer and an IPFS producer differ only in how they store a block and which host string they
 * return. Credentials live in the producer the caller constructs; `publish()` never sees them, and
 * the mirror is never trusted (every byte is re-hash-verified on read — §13).
 */
export interface MirrorProducer {
  /**
   * Store one block so it later resolves under `block.cid`. A conforming producer verifies the
   * store reports/serves that exact CID and REJECTS on any mismatch (mirroring the PDS-side
   * {@link BlobCidMismatchError} guard) — a hint that points at a mirror lacking the blob is dead
   * weight that only wastes a gateway's racing budget.
   */
  pushBlock(block: MirrorBlock): Promise<void>;
  /**
   * The `hints` entries to record once every {@link pushBlock} resolved (§7.3): a bare host for a
   * RASL/CDN mirror (`cdn.example.com`), or `ipfs:<gateway-host>` for IPFS (§13.1). Never called if
   * any push failed.
   */
  hints(): string[];
}

/** Injectable non-determinism, so publish/rollback are deterministic under test. */
export interface Clocks {
  /** Wall clock for `createdAt`; defaults to `() => new Date()`. */
  now?: () => Date;
  /** rkey generator; defaults to a real TID generator. */
  genTid?: () => string;
}

/** Options for {@link publish}. */
export interface PublishInput extends Clocks {
  client: PdsClient;
  built: BuildResult;
  /**
   * rkey of an existing `app.szzt.site` to update. Omit for a first publish (a new site record
   * is created and its rkey returned as part of `siteUri`).
   */
  siteRkey?: string;
  /** Manifest CID of the previous deployment, recorded as `prev` for lineage (§4.3). */
  prevManifestCid?: string;
  /** Entry document path; defaults to `/index.html` (§4.2). */
  entry?: string;
  meta?: SiteMeta;
  /**
   * Optional external mirror producers (BYO CDN/IPFS, §7.3). Each is INDEPENDENT: every resource blob
   * and the manifest are pushed to it after the PDS upload, and on full success its
   * {@link MirrorProducer.hints} are merged onto the site record. Best-effort and per-producer
   * all-or-nothing — one mirror failing (or being incomplete) drops only ITS hint, never another's,
   * and the authoritative PDS publish proceeds regardless. Pass several to advertise several mirrors
   * (e.g. a CDN and IPFS) without coupling their fates.
   */
  producers?: MirrorProducer[];
  /** Max `swapCommit` retries on concurrent-write conflict; defaults to 3. */
  maxRetries?: number;
  /**
   * The target PDS's maximum record size in bytes (§3.7). The encoded manifest is validated against
   * this BEFORE any blob is uploaded, failing fast with {@link ManifestTooLargeError} rather than after
   * every blob is uploaded when `applyWrites` rejects the oversized record. Defaults to
   * {@link MAX_RECORD_BYTES} (atproto's recommended `MAX_CBOR_RECORD_SIZE`, 1 MiB).
   */
  maxRecordBytes?: number;
  /** Opt-in staged publishing: hash-check canonical PDS reads, skip usable blobs, anchor bounded
   * batches in app.szzt.staging, then retrieve every blob before the atomic site switch. Requires
   * client.getBlob. Interrupted runs retain deterministic staging records for same-build resume.
   * Off by default pending live-PDS interoperability testing; no full ATW conformance claim. */
  verifyBlobs?: boolean;
}

/** A deterministic, inert blob anchor owned by a verified publish attempt. */
export interface BlobStageRef {
  uri: string;
  rkey: string;
  cid: string;
}

/** Cleanup is best effort AFTER a successful switch: failure must not pretend publish failed. */
export interface BlobStageCleanup {
  removed: number;
  retained: BlobStageRef[];
}

export interface BlobVerificationReceipt {
  uploaded: number;
  reused: number;
  verified: true;
  stagingCleanup: BlobStageCleanup;
}

/** Result of a successful {@link publish}. */
export interface PublishResult {
  /** AT-URI of the new `app.szzt.deployment` record. */
  deploymentUri: string;
  /** atproto CID of the new deployment record. */
  deploymentCid: string;
  /** AT-URI of the new `ing.dasl.masl` manifest record (the deployment's `tile`). */
  tileUri: string;
  /** atproto CID of the manifest record (the deployment's `tile.cid`). */
  tileCid: string;
  /** AT-URI of the `app.szzt.site` record (created or updated). */
  siteUri: string;
  /** rkey of the `app.szzt.site` record. */
  siteRkey: string;
  /** dCBOR42 root CID of the site (deployment `root` = manifest record `cid`). */
  rootCid: string;
  /** A concurrent publisher moved the pointer before readback. Our immutable chain was verified. */
  superseded?: true;
  /** Present only for opt-in staged blob verification. Not a cryptographic proof receipt. */
  blobVerification?: BlobVerificationReceipt;
}

/** Options for {@link rollback}. Rollback repoints only — no new record, no `createdAt`, no clocks. */
export interface RollbackInput {
  client: PdsClient;
  /** rkey of the `app.szzt.site` to repoint. */
  siteRkey: string;
  /** Strong ref to the prior deployment to make active (§8). */
  target: StrongRef;
  /** Max `swapRecord` retries on concurrent-write conflict; defaults to 3. */
  maxRetries?: number;
}

/** Result of a successful {@link rollback}. */
export interface RollbackResult {
  siteUri: string;
  siteCid: string;
  /** The deployment strong ref now active. */
  current: StrongRef;
}
